/**
 * IndexedDB helper for offline note storage
 * Provides offline-first data layer with sync capabilities
 */

const DB_NAME = 'vaultnotes-offline';
const DB_VERSION = 1;

// Store names
const STORES = {
  NOTES: 'notes',
  NOTEBOOKS: 'notebooks',
  TAGS: 'tags',
  PENDING_SYNC: 'pending_sync',
  META: 'meta',
};

let db = null;

/**
 * Open/initialize the IndexedDB database
 */
export async function initDB() {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[IndexedDB] Failed to open database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      // Recover if connection is closed unexpectedly (e.g., version upgrade from another tab)
      db.onclose = () => { db = null; };
      db.onversionchange = () => { db.close(); db = null; };
      console.log('[IndexedDB] Database opened successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      console.log('[IndexedDB] Upgrading database...');

      // Notes store
      if (!database.objectStoreNames.contains(STORES.NOTES)) {
        const notesStore = database.createObjectStore(STORES.NOTES, { keyPath: 'id' });
        notesStore.createIndex('user_id', 'user_id', { unique: false });
        notesStore.createIndex('updated_at', 'updated_at', { unique: false });
        notesStore.createIndex('is_deleted', 'is_deleted', { unique: false });
      }

      // Notebooks store
      if (!database.objectStoreNames.contains(STORES.NOTEBOOKS)) {
        const notebooksStore = database.createObjectStore(STORES.NOTEBOOKS, { keyPath: 'id' });
        notebooksStore.createIndex('user_id', 'user_id', { unique: false });
      }

      // Tags store
      if (!database.objectStoreNames.contains(STORES.TAGS)) {
        const tagsStore = database.createObjectStore(STORES.TAGS, { keyPath: 'id' });
        tagsStore.createIndex('user_id', 'user_id', { unique: false });
      }

      // Pending sync queue
      if (!database.objectStoreNames.contains(STORES.PENDING_SYNC)) {
        const syncStore = database.createObjectStore(STORES.PENDING_SYNC, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('type', 'type', { unique: false });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Meta store for last sync time, etc.
      if (!database.objectStoreNames.contains(STORES.META)) {
        database.createObjectStore(STORES.META, { keyPath: 'key' });
      }
    };
  });
}

/**
 * Generic store operations
 */
async function getStore(storeName, mode = 'readonly') {
  const database = await initDB();
  const transaction = database.transaction(storeName, mode);
  return transaction.objectStore(storeName);
}

async function getAllFromStore(storeName) {
  const store = await getStore(storeName);
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getByIdFromStore(storeName, id) {
  const store = await getStore(storeName);
  return new Promise((resolve, reject) => {
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function putToStore(storeName, data) {
  const store = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.put(data);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function deleteFromStore(storeName, id) {
  const store = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function clearStore(storeName) {
  const store = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Notes operations
 */
export async function getAllNotes() {
  return getAllFromStore(STORES.NOTES);
}

export async function getNoteById(id) {
  return getByIdFromStore(STORES.NOTES, id);
}

export async function saveNote(note) {
  return putToStore(STORES.NOTES, { ...note, _offline_updated: Date.now() });
}

export async function saveNotes(notes) {
  const database = await initDB();
  const transaction = database.transaction(STORES.NOTES, 'readwrite');
  const store = transaction.objectStore(STORES.NOTES);
  
  return new Promise((resolve, reject) => {
    notes.forEach((note) => {
      store.put(note);
    });
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function deleteNote(id) {
  return deleteFromStore(STORES.NOTES, id);
}

export async function clearNotes() {
  return clearStore(STORES.NOTES);
}

/**
 * Notebooks operations
 */
export async function getAllNotebooks() {
  return getAllFromStore(STORES.NOTEBOOKS);
}

export async function saveNotebook(notebook) {
  return putToStore(STORES.NOTEBOOKS, notebook);
}

export async function saveNotebooks(notebooks) {
  const database = await initDB();
  const transaction = database.transaction(STORES.NOTEBOOKS, 'readwrite');
  const store = transaction.objectStore(STORES.NOTEBOOKS);
  
  return new Promise((resolve, reject) => {
    notebooks.forEach((notebook) => {
      store.put(notebook);
    });
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function clearNotebooks() {
  return clearStore(STORES.NOTEBOOKS);
}

/**
 * Tags operations
 */
export async function getAllTags() {
  return getAllFromStore(STORES.TAGS);
}

export async function saveTag(tag) {
  return putToStore(STORES.TAGS, tag);
}

export async function saveTags(tags) {
  const database = await initDB();
  const transaction = database.transaction(STORES.TAGS, 'readwrite');
  const store = transaction.objectStore(STORES.TAGS);
  
  return new Promise((resolve, reject) => {
    tags.forEach((tag) => {
      store.put(tag);
    });
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function clearTags() {
  return clearStore(STORES.TAGS);
}

/**
 * Pending sync queue operations
 */
export async function addToPendingSync(operation) {
  const syncItem = {
    ...operation,
    timestamp: Date.now(),
  };
  return putToStore(STORES.PENDING_SYNC, syncItem);
}

export async function getPendingSync() {
  return getAllFromStore(STORES.PENDING_SYNC);
}

export async function removePendingSync(id) {
  return deleteFromStore(STORES.PENDING_SYNC, id);
}

export async function clearPendingSync() {
  return clearStore(STORES.PENDING_SYNC);
}

/**
 * Meta operations
 */
export async function getMeta(key) {
  const result = await getByIdFromStore(STORES.META, key);
  return result?.value;
}

export async function setMeta(key, value) {
  return putToStore(STORES.META, { key, value });
}

/**
 * Sync helpers
 */
export async function getLastSyncTime() {
  return getMeta('lastSyncTime');
}

export async function setLastSyncTime(time = Date.now()) {
  return setMeta('lastSyncTime', time);
}

export async function hasPendingChanges() {
  const pending = await getPendingSync();
  return pending.length > 0;
}

/**
 * Clear all offline data (for logout)
 */
export async function clearAllOfflineData() {
  await clearNotes();
  await clearNotebooks();
  await clearTags();
  await clearPendingSync();
  await setMeta('lastSyncTime', null);
  console.log('[IndexedDB] All offline data cleared');
}

/**
 * Export all data for backup
 */
export async function exportOfflineData() {
  const [notes, notebooks, tags] = await Promise.all([
    getAllNotes(),
    getAllNotebooks(),
    getAllTags(),
  ]);
  return { notes, notebooks, tags };
}
